// docs/73 TP4: places at the inn's tables. Each table has a row of seats behind it, far enough
// apart that two sitters never overlap. A meal (mine, or another player's band at the table) is
// given free seats: villagers already sitting keep theirs unless the bands cannot fit otherwise,
// and then those at the long table get up first. Whoever does not get a seat (a beast, a diner too
// many, a villager who gave up their stool) stands aside on a clear bit of floor.
import type { Room } from './layout';
import type { Pt, WalkGrid } from './walk';

export interface Seat extends Pt { table: number }
/** How close (cells along the row) two sitters may be before they overlap. */
export const SEAT_GAP = 0.7;
/** How far apart (cells) people standing aside keep from everyone else. */
export const ASIDE_GAP = 0.9;

/** The seats behind every table (the long table first), left to right. */
export function tableSeats(room: Room): Seat[] {
  const tables = room.tables ?? (room.table ? [room.table] : []);
  return tables.flatMap((t, table) => {
    // a sitter's station is their figure's top-left: from just over the table's left edge to a cell short of its right
    const span = Math.max(0, t.w - 0.8);
    const n = Math.floor(span / SEAT_GAP + 1e-6) + 1;
    const gap = n > 1 ? span / (n - 1) : 0;
    return Array.from({ length: n }, (_, i) => ({ x: t.x - 0.1 + i * gap, y: t.y - 0.75, table }));
  });
}

const sits = (s: Pt, p: Pt) => Math.abs(s.x - p.x) < SEAT_GAP && Math.abs(s.y - p.y) < 0.5;

export interface SeatPlan {
  /** Each group's seats, the far end first (the leader's). Shorter than asked when the tables are full. */
  seats: Map<string, Seat[]>;
  /** The villagers who give up their seat. */
  evicted: string[];
}

/**
 * Seat `groups` (in order: who sat first) at the free seats. `seated` are villagers sitting at a
 * table now. A group keeps to one table where one has room for all of it.
 */
export function seatPlan(seats: readonly Seat[], seated: ReadonlyArray<Pt & { key: string }>, groups: ReadonlyArray<{ key: string; n: number }>): SeatPlan {
  const need = groups.reduce((s, g) => s + g.n, 0);
  let sitting = [...seated];
  const evicted: string[] = [];
  const free = () => seats.filter((s) => !sitting.some((p) => sits(s, p)));
  // the long table's sitters get up first, from its left
  const order = [...seated].filter((p) => seats.some((s) => sits(s, p))).sort((a, b) => tableOf(seats, a) - tableOf(seats, b) || a.x - b.x);
  for (const p of order) {
    if (free().length >= need) break;
    sitting = sitting.filter((q) => q.key !== p.key);
    evicted.push(p.key);
  }
  let left = free();
  const out = new Map<string, Seat[]>();
  for (const g of groups) {
    const byTable = new Map<number, Seat[]>();
    for (const s of left) byTable.set(s.table, [...(byTable.get(s.table) ?? []), s]);
    const whole = [...byTable.entries()].sort((a, b) => a[0] - b[0]).find(([, ss]) => ss.length >= g.n);
    const pick = (whole ? whole[1] : [...left].sort((a, b) => a.table - b.table || a.x - b.x)).slice(0, g.n);
    out.set(g.key, [...pick].sort((a, b) => b.x - a.x));
    left = left.filter((s) => !pick.includes(s));
  }
  return { seats: out, evicted };
}

function tableOf(seats: readonly Seat[], p: Pt): number {
  return seats.find((s) => sits(s, p))?.table ?? 99;
}

/**
 * `n` spots on the floor near `near`, each clear of `taken` and of each other by `ASIDE_GAP`, on
 * the grid's back rows (where people stand about a room). Falls back to `near` when the floor is full.
 */
export function asideSpots(g: WalkGrid, taken: readonly Pt[], near: Pt, n: number): Pt[] {
  const cands: Pt[] = [];
  for (let y = g.y0; y <= Math.min(g.y1, g.y0 + 1) + 1e-6; y += 0.5) for (let x = Math.ceil(g.x0 * 2) / 2; x <= g.x1 + 1e-6; x += 0.5) if (g.free(x, y)) cands.push({ x, y });
  cands.sort((a, b) => Math.hypot(a.x - near.x, (a.y - near.y) * 2) - Math.hypot(b.x - near.x, (b.y - near.y) * 2));
  const all = [...taken];
  const out: Pt[] = [];
  for (const c of cands) {
    if (out.length >= n) break;
    if (all.some((p) => Math.abs(p.x - c.x) < ASIDE_GAP && Math.abs(p.y - c.y) < 0.9)) continue;
    out.push(c);
    all.push(c);
  }
  while (out.length < n) out.push({ ...near });
  return out;
}
